import { useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Ban, KeyRound, SearchX, ServerCrash, ShieldAlert } from 'lucide-react';
import ErrorPage from './ErrorPage';

interface ErrorRouteState {
  requestId?: string | null;
  message?: string;
  from?: string;
}

function useErrorRouteState() {
  const location = useLocation();
  return (location.state || {}) as ErrorRouteState;
}

function useBackOrHomeAction() {
  const navigate = useNavigate();
  const { t } = useTranslation('errors');

  return useMemo(
    () => ({
      label: t('actions.goBack'),
      onClick: () => {
        if (window.history.length > 1) {
          navigate(-1);
          return;
        }
        navigate('/login');
      }
    }),
    [navigate, t]
  );
}

export function BadRequestPage() {
  const { t } = useTranslation('errors');
  const { requestId, message } = useErrorRouteState();
  const secondaryAction = useBackOrHomeAction();

  return (
    <ErrorPage
      statusCode={400}
      title={t('pages.badRequest.title')}
      message={message || t('pages.badRequest.message')}
      requestId={requestId}
      icon={<AlertTriangle className="h-7 w-7" aria-hidden="true" />}
      primaryAction={{ label: t('actions.goHome'), to: '/login' }}
      secondaryAction={secondaryAction}
    />
  );
}

export function UnauthorizedPage() {
  const { t } = useTranslation('errors');
  const { requestId, message, from } = useErrorRouteState();
  const secondaryAction = useBackOrHomeAction();

  return (
    <ErrorPage
      statusCode={401}
      title={t('pages.unauthorized.title')}
      message={message || t('pages.unauthorized.message')}
      requestId={requestId}
      icon={<KeyRound className="h-7 w-7" aria-hidden="true" />}
      primaryAction={{ label: t('actions.goHome'), to: '/login' }}
      secondaryAction={secondaryAction}
      auxiliaryAction={{ label: t('actions.goToLogin'), to: from ? `/login?from=${encodeURIComponent(from)}` : '/login' }}
    />
  );
}

export function ForbiddenPage() {
  const { t } = useTranslation('errors');
  const { requestId, message } = useErrorRouteState();
  const secondaryAction = useBackOrHomeAction();

  return (
    <ErrorPage
      statusCode={403}
      title={t('pages.forbidden.title')}
      message={message || t('pages.forbidden.message')}
      requestId={requestId}
      icon={<Ban className="h-7 w-7" aria-hidden="true" />}
      primaryAction={{ label: t('actions.goHome'), to: '/login' }}
      secondaryAction={secondaryAction}
    />
  );
}

export function NotFoundPage() {
  const { t } = useTranslation('errors');
  const { requestId, message } = useErrorRouteState();
  const secondaryAction = useBackOrHomeAction();

  return (
    <ErrorPage
      statusCode={404}
      title={t('pages.notFound.title')}
      message={message || t('pages.notFound.message')}
      requestId={requestId}
      icon={<SearchX className="h-7 w-7" aria-hidden="true" />}
      primaryAction={{ label: t('actions.goHome'), to: '/login' }}
      secondaryAction={secondaryAction}
    />
  );
}

export function InternalServerErrorPage() {
  const { t } = useTranslation('errors');
  const { requestId, message } = useErrorRouteState();

  return (
    <ErrorPage
      statusCode={500}
      title={t('pages.internalServerError.title')}
      message={message || t('pages.internalServerError.message')}
      requestId={requestId}
      icon={<ServerCrash className="h-7 w-7" aria-hidden="true" />}
      primaryAction={{ label: t('actions.goHome'), to: '/login' }}
      secondaryAction={{
        label: t('actions.retry'),
        onClick: () => window.location.reload()
      }}
    />
  );
}

export function ServiceUnavailablePage() {
  const { t } = useTranslation('errors');
  const { requestId, message } = useErrorRouteState();

  return (
    <ErrorPage
      statusCode={503}
      title={t('pages.serviceUnavailable.title')}
      message={message || t('pages.serviceUnavailable.message')}
      requestId={requestId}
      icon={<ShieldAlert className="h-7 w-7" aria-hidden="true" />}
      primaryAction={{ label: t('actions.goHome'), to: '/login' }}
      secondaryAction={{
        label: t('actions.retry'),
        onClick: () => window.location.reload()
      }}
    />
  );
}

export function ErrorRouteShortcuts() {
  const { t } = useTranslation('errors');

  return (
    <div className="app-shell flex min-h-screen items-center justify-center px-4 py-10 sm:px-6">
      <section className="panel-surface w-full max-w-2xl rounded-3xl border border-slate-200/70 p-6 sm:p-8 dark:border-slate-700">
        <h1 className="section-heading text-2xl text-slate-900 dark:text-slate-100">{t('shortcuts.title')}</h1>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
          {t('shortcuts.description')}
        </p>
        <div className="mt-5 flex flex-wrap gap-2">
          <Link className="btn-primary px-3 py-2 text-sm" to="/error/400">400</Link>
          <Link className="btn-primary px-3 py-2 text-sm" to="/error/401">401</Link>
          <Link className="btn-primary px-3 py-2 text-sm" to="/error/403">403</Link>
          <Link className="btn-primary px-3 py-2 text-sm" to="/error/404">404</Link>
          <Link className="btn-primary px-3 py-2 text-sm" to="/error/500">500</Link>
          <Link className="btn-primary px-3 py-2 text-sm" to="/error/503">503</Link>
        </div>
      </section>
    </div>
  );
}
